import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Dices, Twitter, Flame } from "lucide-react";
import api from "@/lib/api";
import { renderMomentConfig, withXHandle } from "@/lib/memeKit";
import { useSettings } from "@/hooks/useSettings";
import ShareToXDialog from "@/components/ShareToXDialog";

// Homepage "Meme of the Moment" — a fresh $ROBBED meme every hour.
// Rendered 100% locally from a server-cached config. NO AI/LLM calls.
export default function MemeOfMoment() {
  const navigate = useNavigate();
  const { data: settings } = useSettings();
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const viewedRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharePreview, setSharePreview] = useState(null);
  const [canNative, setCanNative] = useState(false);
  const shareBlobRef = useRef(null);
  const shareFileRef = useRef(null);
  const shareTextRef = useRef("");

  const { data, isError } = useQuery({
    queryKey: ["meme-of-moment"],
    queryFn: async () => (await api.get("/meme-of-moment")).data,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const track = (type) => { api.post("/meme-of-moment/event", { type }).catch(() => {}); };
  const trackEvent = (name, meta) => { api.post("/analytics/event", { name, meta: meta || {} }).catch(() => {}); };

  // render when config + image are ready
  useEffect(() => {
    if (!data?.characterSrc || !canvasRef.current) return;
    setReady(false);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => { imgRef.current = img; renderMomentConfig(canvasRef.current, data, img); setReady(true); };
    img.onerror = () => { renderMomentConfig(canvasRef.current, data, null); setReady(true); };
    img.src = data.characterSrc;
  }, [data]);

  // count a view once per load
  useEffect(() => {
    if (data && !viewedRef.current) { viewedRef.current = true; track("view"); }
  }, [data]);

  const makeYourOwn = () => { track("make_your_own"); navigate("/memes"); };
  // Open the share dialog — captures the EXACT current Meme of the Moment (no re-render / no regeneration).
  const openShare = async () => {
    const cap = (data?.caption || []).join(" — ");
    shareTextRef.current = withXHandle((cap ? cap + "\n\n" : "") + "🔥 Meme of the Moment · $ROBBED — THEY GOT RICH. WE GOT ROBBED.", settings?.xUrl);
    let preview = null; try { preview = canvasRef.current?.toDataURL("image/png"); } catch { /* noop */ }
    setSharePreview(preview);
    let blob = null; try { blob = await new Promise((res) => canvasRef.current.toBlob(res, "image/png")); } catch { /* noop */ }
    shareBlobRef.current = blob;
    const file = blob ? new File([blob], "robbed-meme-of-the-moment.png", { type: "image/png" }) : null;
    shareFileRef.current = file;
    setCanNative(!!(file && navigator.canShare && navigator.canShare({ files: [file] })));
    setShareOpen(true);
  };
  // Mobile: REAL image share via Web Share API. Explicit only, never auto-posts.
  const doNativeShare = async () => {
    try {
      await navigator.share({ files: [shareFileRef.current], text: shareTextRef.current });
      track("share");
      trackEvent("share_meme_of_moment_x", { method: "native", image: "attached" }); // verified image share
      setShareOpen(false);
    } catch (err) {
      if (err?.name === "AbortError") return; // user cancelled the native sheet
    }
  };
  // Desktop: text-only composer (X can't auto-attach an image) — record honestly.
  const onManualInitiated = () => {
    track("share");
    trackEvent("share_meme_of_moment_x_manual", { surface: "meme_of_moment", image: "manual" });
  };

  if (isError) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14" data-testid="meme-of-moment">
      <div className="comic-card p-5 sm:p-8 relative overflow-hidden">
        <div className="halftone absolute inset-0 opacity-20 pointer-events-none" />
        <div className="relative grid lg:grid-cols-[1fr_auto] gap-8 items-center">
          <div className="order-2 lg:order-1">
            <span className="tag inline-flex items-center gap-1.5"><Flame className="w-3.5 h-3.5" /> Meme Of The Moment</span>
            <h2 className="headline text-cream text-3xl sm:text-4xl mt-4">
              A FRESH <span className="text-neon">$ROBBED</span> MEME<br />EVERY HOUR.
            </h2>
            <p className="font-mono text-dim mt-3 text-sm max-w-md">
              Auto-rolled from our meme engine — new combo drops every hour. Zero AI, all pain.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <button onClick={makeYourOwn} className="btn-neon" data-testid="mom-make-your-own">
                <Dices className="w-5 h-5" /> Make Your Own
              </button>
              <button onClick={openShare} className="btn-ghost" data-testid="mom-share">
                <Twitter className="w-4 h-4" /> Share on X
              </button>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.92, rotate: -2 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="order-1 lg:order-2 w-full flex justify-center"
          >
            <div className="relative w-full" style={{ maxWidth: 380 }}>
              {!ready && <div className="absolute inset-0 flex items-center justify-center font-mono text-xs text-dim">Rolling a meme…</div>}
              <canvas
                ref={canvasRef} width={1080} height={1080}
                data-testid="mom-canvas"
                className="w-full border-3 border-neon bg-ink3 block"
                style={{ aspectRatio: "1 / 1" }}
              />
            </div>
          </motion.div>
        </div>
      </div>

      <ShareToXDialog
        open={shareOpen} onOpenChange={setShareOpen}
        preview={sharePreview} canNative={canNative}
        blob={shareBlobRef.current} filename="robbed-meme-of-the-moment.png" text={shareTextRef.current}
        onNativeShare={doNativeShare} onManualInitiated={onManualInitiated}
        testidPrefix="mom-share" title="Share Meme of the Moment"
      />
    </section>
  );
}
